import { useCallback } from "react";
import { useFormHandler, useLoggedInAs, usePostHog } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import {
  Address,
  EnumAddressLocationType,
  EnumOwnerType,
  EnumPeopleContactMethod,
  EnumPeopleVisibleToMetadataPhonesType,
  PeopleManySimpleDocument,
  PersonDetailsDocument,
  PersonDetailsQuery,
  usePersonUpdateCommonMutation,
  usePersonUpdateVisibleToMutation,
} from "gql/generated";
import { useTranslation } from "react-i18next";
import { emailRegex, getAddressString, phoneNumberRegex } from "shared/utils";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.People]);

// Return the common address schema
const getAddressSchema = () =>
  z
    .object({
      fullAddress: z.string().optional(),
      address: z
        .object({
          line1: z.string().optional().default(""),
          line2: z.string().optional().default(""),
          city: z.string().optional().default(""),
          subdivision: z.string().optional().default(""),
          postalCode: z.string().optional().default(""),
          country: z.string().optional().default(""),
          location: z
            .object({
              type: z.string().default("Point"),
              coordinates: z.object({
                lat: z.number().min(-90).max(90),
                lng: z.number().min(-180).max(180),
              }),
            })
            .optional(),
        })
        .partial()
        .optional(),
    })
    .optional();

// Returns the schema for a phone number
const getPhoneSchema = () =>
  z
    .string()
    .trim()
    .optional()
    .refine(
      value => {
        if (value) {
          // Look for valid phone numbers
          return phoneNumberRegex.test(value);
        }
        return true;
      },
      {
        message: i18n.t(
          "people:PERSON_CONTACT_INFO_MODAL.COMMON_CONTACT_INFO.ERRORS.PHONE",
        ),
      },
    );

// Returns the schema for a phone extension
const getPhoneExtSchema = () =>
  z
    .string()
    .trim()
    .optional()
    .refine(
      value => {
        if (value) {
          // Look for valid phone numbers
          return /^\d{1,6}$/.test(value);
        }
        return true;
      },
      {
        message: i18n.t(
          "people:PERSON_CONTACT_INFO_MODAL.COMMON_CONTACT_INFO.ERRORS.PHONE_EXT",
        ),
      },
    );

// Returns the schema for a phone number with an extension
const getPhoneWithExtSchema = () =>
  z.object({
    phone: getPhoneSchema(),
    ext: getPhoneExtSchema(),
    type: z.nativeEnum(EnumPeopleVisibleToMetadataPhonesType).optional(),
  });

export const personContactInfoFormSchema = z.object({
  // Names are optional so a person stored without one can still have their
  // other contact details edited. The email field below remains required, so a
  // person always keeps at least one identifier.
  firstName: z.string().trim().optional(),
  lastName: z.string().trim().optional(),
  pronouns: z.string().optional(),
  primaryTitle: z.string().optional(),
  birthday: z.string().optional(),
  contactMethod: z.nativeEnum(EnumPeopleContactMethod).optional(),
  optInEmail: z.boolean().optional(),
  optInPhone: z.boolean().optional(),
  optInSMS: z.boolean().optional(),
  optInFax: z.boolean().optional(),
  email: z
    .string({
      required_error: `${i18n.t("people:PERSON_CONTACT_INFO_MODAL.COMMON_CONTACT_INFO.LABELS.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(6, {
      message: `${i18n.t("people:PERSON_CONTACT_INFO_MODAL.COMMON_CONTACT_INFO.LABELS.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .email({ message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL") }),
  photo: z.string().optional(),
  phone: getPhoneSchema(),
  phoneExt: getPhoneExtSchema(),
  fax: getPhoneSchema(),
  faxExt: getPhoneExtSchema(),
  address: getAddressSchema(),
  mailingAddress: getAddressSchema(),
  emails: z
    .array(
      z
        .string()
        .trim()
        .optional()
        .refine(
          value => {
            if (value) {
              return emailRegex.test(value);
            }
            return true;
          },
          {
            message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL"),
          },
        ),
    )
    .optional(),
  phones: z.array(getPhoneWithExtSchema()).optional(),
});

export type PhoneWithExtValues = z.infer<
  ReturnType<typeof getPhoneWithExtSchema>
>;

export type AddressFormValues = z.infer<ReturnType<typeof getAddressSchema>>;

export type PersonContactInfoFormValues = z.infer<
  typeof personContactInfoFormSchema
>;

/**
 * Get the default values for the contact form
 * @param contact - The contact object to get the default values for
 * @returns The default values for the contact form
 */
const getContactDefaultValue = (address: Address | undefined) => ({
  fullAddress: address ? getAddressString(address) : "",
  address: {
    line1: address?.line1 ? address.line1 : "",
    line2: address?.line2 ? address.line2 : "",
    city: address?.city ? address.city : "",
    subdivision: address?.subdivision ? address.subdivision : "",
    postalCode: address?.postalCode ? address.postalCode : "",
    country: address?.country ? address.country : "",
    location: {
      type: address?.location?.type ? address.location?.type : "Point",
      coordinates: {
        lng: address?.location?.coordinates[0]
          ? address.location.coordinates[0]
          : 0,
        lat: address?.location?.coordinates[1]
          ? address.location.coordinates[1]
          : 0,
      },
    },
  },
});

/**
 * Convert the address form values to an address object
 * @param value - The address form values to convert
 * @returns The address object
 */
const convertAddressFormValuesToAddress = (value: AddressFormValues) => ({
  line1: value?.address?.line1 ?? "",
  line2: value?.address?.line2 ?? "",
  city: value?.address?.city ?? "",
  subdivision: value?.address?.subdivision ?? "",
  postalCode: value?.address?.postalCode ?? "",
  country: value?.address?.country ?? "",
  location: {
    type: EnumAddressLocationType.Point,
    coordinates: [
      value?.address?.location?.coordinates?.lng ?? 0,
      value?.address?.location?.coordinates?.lat ?? 0,
    ],
  },
});

type UsePersonContactInfoFormProps = {
  person: PersonDetailsQuery["personDetails"];
  onSuccess: (didSubmit: boolean) => void;
  onError: (message: string) => void;
  entityContext?: { entityId: string; type: EnumOwnerType };
};

export const usePersonContactInfoForm = ({
  person,
  onSuccess,
  onError,
  entityContext,
}: UsePersonContactInfoFormProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_CONTACT_INFO_MODAL.COMMON_CONTACT_INFO",
  });
  const routeLoggedInAs = useLoggedInAs();
  const loggedInAs = entityContext ?? routeLoggedInAs;

  const defaultValues: PersonContactInfoFormValues = {
    email: person?.personInfo?.email ?? "",
    firstName: person?.personInfo?.firstName ?? "",
    lastName: person?.personInfo?.lastName ?? "",
    photo: person?.personInfo?.photo?._id ?? undefined,
    phone: person?.personInfo?.phone ?? "",
    phoneExt: person?.personInfo?.phoneExt ?? "",
    fax: person?.personInfo?.fax ?? "",
    faxExt: person?.personInfo?.faxExt ?? "",
    optInEmail: person?.personInfo?.optInEmail ?? false,
    optInPhone: person?.personInfo?.optInPhone ?? false,
    optInSMS: person?.personInfo?.optInSMS ?? false,
    optInFax: person?.personInfo?.optInFax ?? false,
    pronouns: person?.personInfo?.pronouns ?? "",
    primaryTitle: person?.personInfo?.primaryTitle ?? "",
    birthday: person?.personInfo?.birthday ?? "",
    contactMethod: (person?.personInfo?.contactMethod ??
      undefined) as EnumPeopleContactMethod,
    address: getContactDefaultValue(person?.personInfo?.address as Address),
    mailingAddress: getContactDefaultValue(
      person?.personInfo?.mailingAddress as Address,
    ),
    emails: person?.emails?.map(email => email ?? "") ?? [],
    phones:
      person?.phones?.map(phone => ({
        phone: phone?.phone ?? "",
        ext: phone?.ext ?? "",
        type: phone?.type as EnumPeopleVisibleToMetadataPhonesType,
      })) ?? [],
  };

  const { trackPostHogException } = usePostHog();

  // Used to update the person's common contact info
  const [updatePersonCommon] = usePersonUpdateCommonMutation();

  // Used to update the Additional phone numbers and email addresses
  const [updatePersonVisibleTo] = usePersonUpdateVisibleToMutation();

  return useFormHandler<PersonContactInfoFormValues>(
    useCallback(
      async data => {
        if (data && data.email && loggedInAs && person) {
          try {
            // If any of the common contact info has been updated, we need to call updatePersonCommon
            const {
              hasUpdatedFirstName,
              hasUpdatedLastName,
              hasUpdatedPronouns,
              hasUpdatedPrimaryTitle,
              hasUpdatedBirthday,
              hasUpdatedContactMethod,
              hasUpdatedEmail,
              hasUpdatedPhoto,
              hasUpdatedPhone,
              hasUpdatedPhoneExt,
              hasUpdatedFax,
              hasUpdatedFaxExt,
              hasUpdatedOptInEmail,
              hasUpdatedOptInPhone,
              hasUpdatedOptInSMS,
              hasUpdatedOptInFax,
              hasUpdatedAddress,
              hasUpdatedMailingAddress,
            } = getHasUpdatedCommon(
              data as PersonContactInfoFormValues,
              person,
            );

            // If new changed emails or phone numbers, we need to call updatePersonVisibleTo
            const filteredEmails = data.emails?.filter(
              email => !!email,
            ) as string[];
            const filteredPhones = data.phones?.filter(
              phone => !!phone?.phone,
            ) as PhoneWithExtValues[];

            const hasUpdatedEmails = getHasUpdatedEmails(
              (person?.emails ?? []) as string[],
              filteredEmails,
            );
            const hasUpdatedPhones = getHasUpdatedPhones(
              (person?.phones ?? []) as PhoneWithExtValues[],
              filteredPhones,
            );

            // We'll dynamically add the refetch queries into the mutators based on the hasUpdated values
            // Only the common firstName, lastName, email would trigger a refetch of PeopleManySimpleDocument
            // And we only need to refetch PersonDetailsDocument once, so if hasUpdatedEmails or hasUpdatedPhones is true,
            // we'll add it to updatePersonVisibleTo, otherwise we'll add it to updatePersonCommon
            const updatePersonCommonRefetchQueries = [];
            const updatePersonVisibleToRefetchQueries = [];

            if (hasUpdatedFirstName || hasUpdatedLastName || hasUpdatedEmail) {
              updatePersonCommonRefetchQueries.push(PeopleManySimpleDocument);
            }
            if (hasUpdatedPhoto) {
              updatePersonCommonRefetchQueries.push(PeopleManySimpleDocument);
            }
            if (hasUpdatedEmails || hasUpdatedPhones) {
              updatePersonVisibleToRefetchQueries.push(PersonDetailsDocument);
            } else {
              updatePersonCommonRefetchQueries.push(PersonDetailsDocument);
            }

            let didSubmit = false;

            // Update the common contact info
            if (
              hasUpdatedFirstName ||
              hasUpdatedLastName ||
              hasUpdatedPronouns ||
              hasUpdatedPrimaryTitle ||
              hasUpdatedBirthday ||
              hasUpdatedContactMethod ||
              hasUpdatedEmail ||
              hasUpdatedPhoto ||
              hasUpdatedPhone ||
              hasUpdatedPhoneExt ||
              hasUpdatedFax ||
              hasUpdatedFaxExt ||
              hasUpdatedOptInEmail ||
              hasUpdatedOptInPhone ||
              hasUpdatedOptInSMS ||
              hasUpdatedOptInFax ||
              hasUpdatedAddress ||
              hasUpdatedMailingAddress
            ) {
              const { errors: personUpdateCommonErrors } =
                await updatePersonCommon({
                  variables: {
                    id: person._id,
                    entityId: loggedInAs.entityId,
                    type: loggedInAs.type as EnumOwnerType,
                    record: {
                      ...(hasUpdatedFirstName && { firstName: data.firstName }),
                      ...(hasUpdatedLastName && { lastName: data.lastName }),
                      ...(hasUpdatedPronouns && { pronouns: data.pronouns }),
                      ...(hasUpdatedPrimaryTitle && {
                        primaryTitle: data.primaryTitle,
                      }),
                      ...(hasUpdatedBirthday && { birthday: data.birthday }),
                      ...(hasUpdatedContactMethod && {
                        contactMethod:
                          data.contactMethod as EnumPeopleContactMethod,
                      }),
                      ...(hasUpdatedEmail && { email: data.email }),
                      ...(hasUpdatedPhoto && { photo: data.photo }),
                      ...(hasUpdatedPhone && { phone: data.phone }),
                      ...(hasUpdatedPhoneExt && { phoneExt: data.phoneExt }),
                      ...(hasUpdatedFax && { fax: data.fax }),
                      ...(hasUpdatedFaxExt && { faxExt: data.faxExt }),
                      ...(hasUpdatedOptInEmail && {
                        optInEmail: data.optInEmail,
                      }),
                      ...(hasUpdatedOptInPhone && {
                        optInPhone: data.optInPhone,
                      }),
                      ...(hasUpdatedOptInSMS && { optInSMS: data.optInSMS }),
                      ...(hasUpdatedOptInFax && { optInFax: data.optInFax }),
                      ...(hasUpdatedAddress && {
                        address: convertAddressFormValuesToAddress(
                          data.address as AddressFormValues,
                        ),
                      }),
                      ...(hasUpdatedMailingAddress && {
                        mailingAddress: convertAddressFormValuesToAddress(
                          data.mailingAddress as AddressFormValues,
                        ),
                      }),
                    },
                  },
                  refetchQueries: updatePersonCommonRefetchQueries,
                });

              didSubmit = true;

              if (personUpdateCommonErrors) {
                throw new Error(personUpdateCommonErrors[0].message);
              }
            }

            // Update the visibleTo info
            if (hasUpdatedEmails || hasUpdatedPhones) {
              const { errors: personUpdateVisibleToErrors } =
                await updatePersonVisibleTo({
                  variables: {
                    id: person._id,
                    entityId: loggedInAs.entityId,
                    type: loggedInAs.type as EnumOwnerType,
                    metadata: {
                      ...(hasUpdatedEmails &&
                        data.emails && {
                          emails: filteredEmails,
                        }),
                      ...(hasUpdatedPhones &&
                        data.phones && {
                          phones: filteredPhones,
                        }),
                    },
                  },
                  refetchQueries: updatePersonVisibleToRefetchQueries,
                });

              didSubmit = true;

              if (personUpdateVisibleToErrors) {
                throw new Error(personUpdateVisibleToErrors[0].message);
              }
            }

            onSuccess(didSubmit);
          } catch (error) {
            trackPostHogException(error, {
              fileName: "usePersonContactInfoForm.ts",
              method: "handleSubmit",
              message: "Error submitting person contact info form",
            });
            onError(t("ERRORS.CREATE_PERSON"));
          }
        }
      },
      [
        loggedInAs,
        person,
        t,
        onError,
        onSuccess,
        updatePersonCommon,
        updatePersonVisibleTo,
        trackPostHogException,
      ],
    ),
    defaultValues,
    { schema: personContactInfoFormSchema, mode: "onBlur" },
  );
};

/**
 * Check if the common contact info has been updated
 * @param data - The form data
 * @param person - The person
 * @returns The hasUpdated values
 */
const getHasUpdatedCommon = (
  data: PersonContactInfoFormValues,
  person: NonNullable<PersonDetailsQuery["personDetails"]>,
) => ({
  hasUpdatedFirstName: data.firstName !== person.personInfo?.firstName,
  hasUpdatedLastName: data.lastName !== person.personInfo?.lastName,
  hasUpdatedPronouns: data.pronouns !== person.personInfo?.pronouns,
  hasUpdatedPrimaryTitle: data.primaryTitle !== person.personInfo?.primaryTitle,
  hasUpdatedBirthday: data.birthday !== person.personInfo?.birthday,
  hasUpdatedContactMethod:
    data.contactMethod &&
    data.contactMethod !== person.personInfo?.contactMethod,
  hasUpdatedEmail: data.email !== person.personInfo?.email,
  hasUpdatedPhoto: data.photo !== (person.personInfo?.photo?._id ?? undefined),
  hasUpdatedPhone: data.phone !== person.personInfo?.phone,
  hasUpdatedPhoneExt: data.phoneExt !== person.personInfo?.phoneExt,
  hasUpdatedFax: data.fax !== person.personInfo?.fax,
  hasUpdatedFaxExt: data.faxExt !== person.personInfo?.faxExt,
  hasUpdatedOptInEmail: data.optInEmail !== person.personInfo?.optInEmail,
  hasUpdatedOptInPhone: data.optInPhone !== person.personInfo?.optInPhone,
  hasUpdatedOptInSMS: data.optInSMS !== person.personInfo?.optInSMS,
  hasUpdatedOptInFax: data.optInFax !== person.personInfo?.optInFax,
  hasUpdatedAddress:
    data.address?.fullAddress !==
      getAddressString((person.personInfo?.address ?? {}) as Address) ||
    data.address?.address?.line1 !== person.personInfo?.address?.line1 ||
    data.address?.address?.line2 !== person.personInfo?.address?.line2 ||
    data.address?.address?.city !== person.personInfo?.address?.city ||
    data.address?.address?.subdivision !==
      person.personInfo?.address?.subdivision ||
    data.address?.address?.postalCode !==
      person.personInfo?.address?.postalCode ||
    data.address?.address?.country !== person.personInfo?.address?.country ||
    data.address?.address?.location?.type !==
      person.personInfo?.address?.location?.type ||
    data.address?.address?.location?.coordinates?.lat !==
      person.personInfo?.address?.location?.coordinates[1] ||
    data.address?.address?.location?.coordinates?.lng !==
      person.personInfo?.address?.location?.coordinates[0],
  hasUpdatedMailingAddress:
    data.mailingAddress?.fullAddress !==
      getAddressString((person.personInfo?.mailingAddress ?? {}) as Address) ||
    data.mailingAddress?.address?.line1 !==
      person.personInfo?.mailingAddress?.line1 ||
    data.mailingAddress?.address?.line2 !==
      person.personInfo?.mailingAddress?.line2 ||
    data.mailingAddress?.address?.city !==
      person.personInfo?.mailingAddress?.city ||
    data.mailingAddress?.address?.subdivision !==
      person.personInfo?.mailingAddress?.subdivision ||
    data.mailingAddress?.address?.postalCode !==
      person.personInfo?.mailingAddress?.postalCode ||
    data.mailingAddress?.address?.country !==
      person.personInfo?.mailingAddress?.country ||
    data.mailingAddress?.address?.location?.type !==
      person.personInfo?.mailingAddress?.location?.type ||
    data.mailingAddress?.address?.location?.coordinates?.lat !==
      person.personInfo?.mailingAddress?.location?.coordinates[1] ||
    data.mailingAddress?.address?.location?.coordinates?.lng !==
      person.personInfo?.mailingAddress?.location?.coordinates[0],
});

/**
 * Check if the Additional emails have been updated
 * @param initialEmails - The initial emails
 * @param newEmails - The new emails
 * @returns True if the emails have been updated, false otherwise
 */
const getHasUpdatedEmails = (initialEmails: string[], newEmails: string[]) =>
  initialEmails.length !== newEmails.length ||
  initialEmails.some((email, index) => email !== newEmails[index]);

/**
 * Check if the Additional phone numbers have been updated
 * @param initialPhones - The initial phone numbers
 * @param newPhones - The new phone numbers
 * @returns True if the phone numbers have been updated, false otherwise
 */
const getHasUpdatedPhones = (
  initialPhones: PhoneWithExtValues[],
  newPhones: PhoneWithExtValues[],
) =>
  initialPhones.length !== newPhones.length ||
  initialPhones.some(
    (phone, index) =>
      phone.phone !== newPhones[index]?.phone &&
      phone.ext !== newPhones[index]?.ext &&
      phone.type !== newPhones[index]?.type,
  );
